import { useEffect, useState, useCallback } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

export function SellerReviews({ sellerId }) {
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!sellerId) return;
    api.get(`/sellers/${sellerId}/reviews`).then((r) => setData(r.data)).catch(() => {});
  }, [sellerId]);
  useEffect(() => { load(); }, [load]);

  const submit = async (sentiment) => {
    setBusy(true);
    try {
      await api.post(`/sellers/${sellerId}/review`, { sentiment, comment: comment.trim() || null });
      toast.success("Thanks, your feedback has been saved");
      setComment("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save feedback");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid="seller-reviews">
      <div className="flex items-center gap-4 text-sm">
        <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="seller-positive-count"><ThumbsUp size={15} /> {data.positive} positive</span>
        <span className="inline-flex items-center gap-1 text-red-600" data-testid="seller-negative-count"><ThumbsDown size={15} /> {data.negative} negative</span>
      </div>

      {data.can_review && (
        <div className="mt-3 space-y-2" data-testid="leave-feedback">
          <p className="text-xs text-slate-500">{data.my_review ? "Update your feedback for this seller" : "Leave feedback for this seller"}</p>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Optional comment…" data-testid="feedback-comment" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={busy} data-testid="feedback-positive" onClick={() => submit("positive")}
              className={`flex-1 ${data.my_review === "positive" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "text-emerald-700 border-emerald-200"}`}>
              <ThumbsUp size={15} className="mr-1" /> Positive
            </Button>
            <Button size="sm" variant="outline" disabled={busy} data-testid="feedback-negative" onClick={() => submit("negative")}
              className={`flex-1 ${data.my_review === "negative" ? "border-red-500 bg-red-50 text-red-600" : "text-red-600 border-red-200"}`}>
              <ThumbsDown size={15} className="mr-1" /> Negative
            </Button>
          </div>
        </div>
      )}

      {data.reviews.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="seller-reviews-list">
          {data.reviews.filter((r) => r.comment).slice(0, 5).map((r, i) => (
            <div key={`${r.reviewer_name}-${r.created_at}-${i}`} className="flex items-start gap-2 text-sm">
              {r.sentiment === "positive"
                ? <ThumbsUp size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                : <ThumbsDown size={14} className="text-red-500 mt-0.5 shrink-0" />}
              <p className="text-slate-600"><span className="font-medium text-slate-800">{r.reviewer_name}</span>: {r.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
